<template>
  <Modal ref="modal" @show="cargar">
    <h2 class="box__title extras-mg-adjuntos__titulo">
      Adjuntos de «{{ datos?.tabla?.nombre || table.name }}»
    </h2>
    <p class="extras-mg-adjuntos__sub">
      Quién puede abrir y bajar los archivos de esta tabla. Quien no puede sigue
      viendo la fila y el nombre del archivo, pero no el archivo.
    </p>

    <Error :error="error"></Error>

    <div v-if="cargando && !datos" class="loading"></div>

    <form v-else-if="datos" @submit.prevent="guardar">
      <div class="extras-mg-adjuntos__modos">
        <Radio v-model="modo" :value="TABLA" class="extras-mg-adjuntos__modo">
          <span class="extras-mg-adjuntos__nombre">Quien ve la tabla</span>
          <span class="extras-mg-adjuntos__descripcion">
            Como siempre: los abre cualquiera que pueda ver la tabla.
          </span>
        </Radio>
        <Radio v-model="modo" :value="LISTA" class="extras-mg-adjuntos__modo">
          <span class="extras-mg-adjuntos__nombre">
            Solo admins y estas personas
          </span>
          <span class="extras-mg-adjuntos__descripcion">
            Los admins del espacio siempre; de los demás, solo los que marques.
            Tampoco se ven por una vista pública.
          </span>
        </Radio>
      </div>

      <template v-if="modo === LISTA">
        <FormInput
          v-if="datos.miembros.length > 8"
          v-model="filtro"
          class="extras-mg-adjuntos__filtro"
          icon-left="iconoir-search"
          placeholder="Buscar persona..."
        />
        <ul class="extras-mg-adjuntos__lista">
          <li
            v-for="m in miembrosFiltrados"
            :key="m.id"
            class="extras-mg-adjuntos__fila"
          >
            <Checkbox
              :model-value="m.admin || elegidas.includes(m.id)"
              :disabled="m.admin"
              @update:model-value="alternar(m.id, $event)"
            >
              <span class="extras-mg-adjuntos__quien">
                {{ m.nombre || m.correo }}
                <span class="extras-mg-adjuntos__correo">
                  {{ m.correo }}{{ m.admin ? ' · admin, siempre' : '' }}
                </span>
              </span>
            </Checkbox>
          </li>
        </ul>
      </template>

      <div class="extras-mg-adjuntos__acciones">
        <Button
          type="primary"
          :loading="guardando"
          :disabled="guardando || sinCambios"
        >
          Guardar
        </Button>
      </div>
    </form>
  </Modal>
</template>

<script>
/**
 * "Acceso a los adjuntos" de una tabla (extras_mg/api/archivos). Solo lo abre
 * el admin del espacio; el servidor lo vuelve a comprobar. No hace falta avisar
 * en vivo a nadie: cada archivo se decide al abrirlo, así que el cambio vale
 * desde el siguiente clic.
 */
import modal from '@baserow/modules/core/mixins/modal'
import error from '@baserow/modules/core/mixins/error'
import AdjuntosService from '@extras_mg/services/adjuntos'

const TABLA = 'TABLA'
const LISTA = 'LISTA'

export default {
  name: 'AccesoAdjuntosModal',
  mixins: [modal, error],
  props: {
    table: { type: Object, required: true },
  },
  data() {
    return {
      TABLA,
      LISTA,
      datos: null,
      cargando: false,
      guardando: false,
      modo: TABLA,
      elegidas: [],
      filtro: '',
    }
  },
  computed: {
    miembrosFiltrados() {
      const f = this.filtro.trim().toLowerCase()
      const lista = this.datos?.miembros || []
      if (!f) return lista
      return lista.filter((m) =>
        `${m.nombre} ${m.correo}`.toLowerCase().includes(f)
      )
    },
    sinCambios() {
      if (!this.datos || this.modo !== this.datos.modo) return false
      if (this.modo === TABLA) return true
      const antes = [...this.datos.personas].sort().join(',')
      return antes === [...this.elegidas].sort().join(',')
    },
  },
  methods: {
    /** El servidor contesta con un `detail` ya en español: se enseña ese. */
    mostrarError(e) {
      const detalle = e?.response?.data?.detail
      if (detalle && typeof detalle === 'string') {
        e.handler?.handled?.()
        this.showError('No se pudo', detalle)
      } else {
        this.handleError(e)
      }
    },
    tomar(datos) {
      this.datos = datos
      this.modo = datos.modo
      this.elegidas = [...datos.personas]
    },
    alternar(id, marcada) {
      this.elegidas = marcada
        ? [...new Set([...this.elegidas, id])]
        : this.elegidas.filter((e) => e !== id)
    },
    async cargar() {
      this.cargando = true
      this.datos = null
      this.filtro = ''
      this.hideError()
      try {
        const { data } = await AdjuntosService(this.$client).obtener(
          this.table.id
        )
        this.tomar(data)
      } catch (e) {
        this.mostrarError(e)
      }
      this.cargando = false
    },
    async guardar() {
      this.guardando = true
      this.hideError()
      try {
        const { data } = await AdjuntosService(this.$client).guardar(
          this.table.id,
          this.modo,
          this.modo === LISTA ? this.elegidas : []
        )
        this.tomar(data)
        this.$store.dispatch('toast/success', {
          title: 'Acceso a los adjuntos',
          message:
            data.modo === TABLA
              ? `«${data.tabla.nombre}»: los abre quien ve la tabla.`
              : `«${data.tabla.nombre}»: solo admins${
                  data.personas.length
                    ? ` y ${data.personas.length} persona${data.personas.length === 1 ? '' : 's'}`
                    : ''
                }.`,
        })
        this.hide()
      } catch (e) {
        this.mostrarError(e)
      }
      this.guardando = false
    },
  },
}
</script>

<style lang="scss">
/* Sin colores fijos: build-theme.py invierte cualquier hex del CSS compilado,
   así que todo sale de currentColor y opacidades y sirve en claro y oscuro. */
.extras-mg-adjuntos__titulo {
  margin-bottom: 4px;
  overflow-wrap: anywhere;
}

.extras-mg-adjuntos__sub {
  font-size: 13px;
  margin: 0 0 16px;
  opacity: 0.7;
}

.extras-mg-adjuntos__modos {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 16px;
}

.extras-mg-adjuntos__modo {
  align-items: flex-start;
}

/* Pegado a su botón, como en "Permisos de la columna". */
.extras-mg-adjuntos__modo .radio__label {
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  gap: 2px;
  text-align: left;
}

.extras-mg-adjuntos__nombre {
  font-size: 14px;
  font-weight: 500;
}

.extras-mg-adjuntos__descripcion,
.extras-mg-adjuntos__correo {
  font-size: 12px;
  opacity: 0.65;
}

.extras-mg-adjuntos__filtro {
  margin-bottom: 8px;
}

.extras-mg-adjuntos__lista {
  list-style: none;
  margin: 0;
  max-height: 320px;
  overflow-y: auto;
  padding: 0;
}

.extras-mg-adjuntos__fila {
  padding: 6px 0;
}

.extras-mg-adjuntos__quien {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.extras-mg-adjuntos__acciones {
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
}
</style>
